<template>
  <div>
    <div class="col-12 px-0">
      <h1 class="d-flex align-items-center">
        <span class="icon mr-2 icon-editieren" aria-hidden="true"></span>
        {{ $t('views.timeSlotEdit.title') }}
      </h1>
    </div>

    <link-back :to="{ name: 'admin-time-slots', params: { id: roomId } }" i18nMessage="common.backToOverview" class="mt-2" />

    <loading-container :state="state" :i18nErrorMessage="$t('views.timeSlotEdit.errorGeneral')" >
      <confirmation-message
        i18nMessage="views.timeSlotEdit.confirmDelete"
        :i18nParams="[room.name, timeSlot.date, timeSlot.startTime, timeSlot.endTime]"
        confirmButtonType="delete"
        v-on:canceled="showDeleteConfirmation = false"
        v-on:confirmed="deleteTimeSlot()"
        v-if="showDeleteConfirmation"
      />
      <form @submit.prevent="onSubmit()" v-if="!showDeleteConfirmation">

        <dl class="w-100">
          <dt>{{ $t('views.timeSlotEdit.room') }}</dt>
          <dd>{{ room.name || '' }}</dd>
          <dt>{{ $t('views.timeSlotEdit.date') }}</dt>
          <dd>{{ timeSlotDateFormatted || '' }}</dd>
        </dl>

        <div class="row mx-auto mt-3">
          <div class="col-12 col-md-4 d-flex bg-lightblue p-3 kapazitaetrow">
            <control-time
              i18nLabel="common.timeSlot.startTime"
              :validation="v$.startTime"
              v-model="startTime"
              :showBreak="true"
              class=" d-flex flex-wrap align-items-center mr-3"
            />
          </div>
          <div class="col-12 col-md-4 d-flex bg-lightblue p-3 kapazitaetrow">
            <control-time
              i18nLabel="common.timeSlot.endTime"
              :validation="v$.endTime"
              v-model="endTime"
              :showBreak="true"
              class=" d-flex flex-wrap align-items-center mr-3"
            />
          </div>
          <div class="col-12 col-md-4 d-flex bg-lightblue p-3 kapazitaetrow">
            <control-integer
              i18nLabel="common.timeSlot.capacity"
              :validation="v$.capacity"
              v-model="capacity"
              :showBreak="true"
              class=" d-flex flex-wrap align-items-center mr-3"
            />
          </div>
        </div>

        <div class="row mx-auto" v-if="tickets.length !== 0">
          <div class="col-12 d-flex align-items-center bg-danger-teres p-3 justify-content-center my-3">
            <feedback-message :message="$t('views.timeSlotEdit.notDeletable', { count: tickets.length })" />
          </div>
        </div>

        <div class="row mx-auto mt-4">
          <div class="col-12 col-md-4 col-lg-3 offset-lg-1 mt-1 px-xs-0">
            <button-cancel :to="{ name: 'admin-time-slots', params: { id: roomId } }" class="btn-w-100" />
          </div>
          <div class="col-12 col-md-4 col-lg-3 mt-1 px-xs-0" v-if="tickets.length === 0">
            <button-delete i18nMessage="common.timeSlot.delete" v-on:clicked="showDeleteConfirmation = true" class="btn-w-100" />
          </div>
          <div class="col-12 col-md-4 col-lg-3 mt-1 px-xs-0">
            <button-save i18nMessage="views.timeSlotEdit.save" class="btn-w-100" />
          </div>
        </div>

        <h2 class="mt-5 mb-2">{{ $t('views.timeSlotEdit.reservations') }} ({{tickets.length}})</h2>

        <link-add-element class="d-flex justify-content-end mb-2" :to="{ name: 'admin-time-slot-book', params: { roomId: roomId, timeSlotId: timeSlotId }, query: { fromSearch: 'y' }}" i18nMessage="views.timeSlotEdit.addReservation"  v-if="timeSlotInPast" />

        <ul class="row list-unstyled mx-auto mt-0 mb-2 p-inline-start-0 adminreservation mt-3" v-if="timeSlotInPast">
          <li v-for="(ticket, index) in tickets" :key="index" class="col-12">
            <router-link :to="{ name: 'admin-reservation-edit', params: { id: ticket.id }, query: { 'fromSearch': 'y' } }">{{ ticket.firstName }} {{ ticket.lastName }}</router-link>
          </li>
        </ul>




      </form>
    </loading-container>
  </div>
</template>

<script lang="ts">
import ControlInteger from '@/components/controls/ControlInteger.vue';
import ControlTime from '@/components/controls/ControlTime.vue';
import ConfirmationMessage from '@/components/feedback/ConfirmationMessage.vue';
import ButtonCancel from '@/components/interaction/ButtonCancel.vue';
import ButtonDelete from '@/components/interaction/ButtonDelete.vue';
import ButtonSave from '@/components/interaction/ButtonSave.vue';
import LoadingContainer from '@/components/structure/LoadingContainer.vue';
import { Room, Ticket, TimeSlot, UpdateTimeSlot } from '@/generated/openapi';
import LoadingMixin from '@/mixins/LoadingMixin';
import { adminService } from '@/services/adminService';
import { formatDateString } from '@/utils/formatUtils';
import { validateAndFocusInvalid } from '@/utils/formUtils';
import { LoadingState } from '@/utils/loadingUtils';
import { smartBack, urlParamString } from '@/utils/navigationUtils';
import { isAfterStartTime, requiredInteger, requiredTime } from '@/utils/validationUtils';
import useVuelidate from '@vuelidate/core';
import { between } from '@vuelidate/validators';
import { defineComponent } from 'vue';
import LinkBack from '@/components/interaction/LinkBack.vue'
import FeedbackMessage from '@/components/feedback/FeedbackMessage.vue'
import { InfoPopupBase, InfoPopupType } from '@/models/InfoPopupData';
import LinkAddElement from '@/components/interaction/LinkAddElement.vue';
import { now, asDateTime } from '@/utils/dateTimeUtils';


export default defineComponent({
  name: 'TimeSlotEdit',
  mixins: [
    LoadingMixin
  ],
  components: {
    LoadingContainer,
    ControlInteger,
    ControlTime,
    ButtonCancel,
    ButtonSave,
    ButtonDelete,
    ConfirmationMessage,
    LinkBack,
    FeedbackMessage,
    LinkAddElement
  },
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  data() {
    return {
      roomId: '',
      timeSlotId: '',
      room: {} as Room,
      timeSlot: {} as TimeSlot,
      startTime: '',
      endTime: '',
      capacity: 0,
      showDeleteConfirmation: false,
      tickets: [] as Ticket[]
    }
  },
  validations() {
    return {
      startTime: {
        requiredTime
      },
      endTime: {
        requiredTime,
        isAfterStartTime
      },
      capacity: {
        requiredInteger,
        between: between(0, 99999)
      }
    }
  },
  created() {
    this.roomId = urlParamString(this.$route, 'roomId')
    this.timeSlotId = urlParamString(this.$route, 'timeSlotId')
    Promise.all([
      adminService.getRoom(this.roomId),
      adminService.getTimeSlot(this.roomId, this.timeSlotId),
      adminService.findTicketsByTimeSlot(this.timeSlotId)
    ]).then(([room, timeSlot, tickets]) => {
      this.room = room
      this.timeSlot = timeSlot
      this.startTime = timeSlot.startTime
      this.endTime = timeSlot.endTime
      this.capacity = timeSlot.capacity || 0
      this.tickets = tickets.content || []
      this.state = LoadingState.success
    }).catch(this.handleError)
  },
  computed: {
    timeSlotDateFormatted(): string {
      return formatDateString(this.timeSlot.date);
    },
    timeSlotInPast(): boolean {
      if(asDateTime(now().toISODate()) > asDateTime(this.timeSlot.date) ){
        return false
      }else{
        return true
      }
    }
  },
  methods: {
    onSubmit() {
      validateAndFocusInvalid(this.v$, this.$el).then(this.updateTimeSlot)
    },
    asTimeSlot(): UpdateTimeSlot {
      return {
        startTime: this.startTime,
        endTime: this.endTime,
        capacity: this.capacity
      }
    },
    updateTimeSlot() {
      this.state = LoadingState.loading;
      adminService.updateTimeSlot(this.room.id, this.timeSlot.id, this.asTimeSlot()).then(() => {
        this.$store.commit('addInfoPopup', {
          title: this.$t('views.timeSlotEdit.confirmation.change'),
          type: InfoPopupType.info
        } as InfoPopupBase)
        smartBack(this.$router, {
          name: 'admin-time-slots',
          params: { id: this.room.id }
        })
      }).catch(this.handleError)
    },
    deleteTimeSlot() {
      this.state = LoadingState.loading;
      adminService.deleteTimeSlot(this.room.id, this.timeSlot.id).then(() => {
        this.$store.commit('addInfoPopup', {
          title: this.$t('views.timeSlotEdit.confirmation.delete'),
          type: InfoPopupType.info
        } as InfoPopupBase)
        smartBack(this.$router, {
          name: 'admin-time-slots',
          params: { id: this.room.id }
        })
      }).catch(this.handleErrorWithI18nString)
    }

  }
});
</script>

<style scoped>
dt {
  float: left;
  font-weight: bold;
  margin-right: 0.5em;
}
dd::after {
  clear: left;
  content: " ";
  display: block;
}
@media screen and (max-width: 420px){
  dt{
    width: 100%;
  }
  dd{
    width: 100%;
    margin-inline-start: 0px;
  }
}
</style>
